import { createReducer } from "@reduxjs/toolkit";

import {
  createLivestreamSuccess,
  createLivestreamFailure,
  createLivestream,
  endLivestream,
  endLivestreamSuccess,
  endLivestreamFailure,
  getLivestreams,
  getLivestreamsSuccess,
  getLivestreamsFailure,
  createLivestreamReset,
  joinLivestream,
  joinLivestreamSuccess,
  joinLivestreamFailure,
  updateLivestreamFailure,
  updateLivestreamSuccess,
  sendMessage,
  sendMessageSuccess,
  sendMessageFailure,
  getMessages,
  getMessagesSuccess,
  getMessagesFailure,
  getParticipants,
  getParticipantsSuccess,
  getParticipantsFailure,
  getNewMessageSuccess,
  getNewTipSuccess,
  getLivestreamDetailsSuccess,
  getUpdatedLivestreamDetailsSuccess,
  leaveLivestream,
  leaveLivestreamSuccess,
  leaveLivestreamFailure,
  livestreamEndedSuccess,
  deleteMessageSuccess,
  changeParticipantRoleSuccess,
  deleteAllParticipantMessagesSuccess,
  handleDeleteMessageSuccess,
  handleDeleteAllParticipantMessagesSuccess,
  banParticipantSuccess,
  handleBanParticipantSuccess,
  handleMuteParticipantSuccess,
  getParticipantsUpdateSuccess,
  resetRoleWasChangedFlag,
  setTipsList,
  resetActiveStream,
  resetLivestreams,
  unmuteMe,
  getTopTippersSuccess,
  getInviteList,
  getInviteListSuccess,
  getInviteListFailure,
  inviteParticipant,
  inviteParticipantSuccess,
  inviteParticipantFailure,
  handleInviteFromStreamerToViewerSuccess,
  acceptInvite,
  acceptInviteSuccess,
  acceptInviteFailure,
  rejectInvite,
  rejectInviteSuccess,
  rejectInviteFailure,
  requestInviteToCoStreaming,
  requestInviteToCoStreamingSuccess,
  requestInviteToCoStreamingFailure,
  updateLivestreamGoal,
  updateLivestreamGoalSuccess,
  updateLivestreamGoalFailure,
  deleteLivestreamGoal,
  deleteLivestreamGoalSuccess,
  deleteLivestreamGoalFailure,
  handleHostAcceptedRequestToCoStreamingSuccess,
  makeCoStreamerAViewer,
  makeCoStreamerAViewerSuccess,
  makeCoStreamerAViewerFailure,
  handleChangeCoStreamerRoleToViewerSuccess,
  rejectAll,
  rejectAllSuccess,
  rejectAllFailure,
  handleCoStreamerStartsStreamSuccess,
} from "./actions";
import {
  BANNED_ERROR_CODE,
  livestreamStatuses,
  messageTypes,
} from "@const/livestreams";
import {
  makeButtonInvitableActionType,
  updateSpinTheBottleActionType,
} from "@redux/livestreams/actionTypes";
import { removeEmptyKeys } from "@helpers/common";

const updateParticipant = (state, payload) => {
  return {
    ...state,
    activeStream: {
      ...state.activeStream,
      streamParticipant: {
        ...state.activeStream.streamParticipant,
        ...payload.participant,
      },
    },
  };
};

const initialState = {
  isLoading: false,
  activeStream: {
    streamGoal: {
      goal: 0,
      amount: 0,
      isTipsIncluded: false,
      description: "",
    },
    lastSpinTheBottleEvent: null,
  },
  livestreams: {
    allLivestreams: [],
    livestreamsById: {},
  },
  chat: {
    messages: [],
    isLoading: false,
  },
  tips: [],
  topTippers: [],
  participants: {
    participantsById: {},
    allParticipants: [],
    isLoading: false,
  },
  inviteList: {
    isLoading: false,
    isInviting: false,
    list: [],
  },
  invites: [],
  lastAnimationFinishedAt: null,
  lastAnimationCategory: null,
  pagination: {},
  isJoining: false,
};

export default createReducer(initialState, (builder) => {
  builder
    //createLivestream
    .addCase(createLivestream.type, (state) => ({
      ...state,
      activeStream: { ...state.activeStream, isLoading: true },
    }))
    .addCase(createLivestreamSuccess.type, (state, { payload }) => {
      return {
        ...state,
        activeStream: {
          ...payload,
          isLoading: false,
          isSuccess: true,
        },
      };
    })
    .addCase(createLivestreamReset.type, (state) => ({
      ...state,
      activeStream: {
        ...state.activeStream,
        isLoading: false,
        isSuccess: false,
      },
    }))
    .addCase(createLivestreamFailure.type, (state, action) => ({
      ...state,
      activeStream: {
        ...state.activeStream,
        isLoading: false,
        isSuccess: false,
      },
      error: action.payload,
    }))

    //endLivestream
    .addCase(endLivestream.type, (state) => ({
      ...state,
      activeStream: {
        ...state.activeStream,
        isLoading: true,
      },
    }))
    .addCase(endLivestreamSuccess.type, (state) => ({
      ...state,
      activeStream: {
        ...state.activeStream,
        token: null,
        status: livestreamStatuses.ended,
      },
    }))
    .addCase(endLivestreamFailure.type, (state, action) => ({
      ...state,
      activeStream: {
        ...state.activeStream,
        isLoading: false,
      },
      error: action.payload,
    }))
    //updateLivestream
    .addCase(updateLivestreamSuccess.type, (state, { payload }) => ({
      ...state,
      activeStream: {
        ...state.activeStream,
        ...payload,
        isLoading: false,
      },
    }))
    .addCase(updateLivestreamFailure.type, (state, action) => ({
      ...state,
      activeStream: {
        ...state.activeStream,
        isLoading: false,
      },
      error: action.payload,
    }))
    //getLivestreams
    .addCase(getLivestreams.type, (state) => {
      return {
        ...state,
        isLoading: true,
      };
    })
    .addCase(getLivestreamsSuccess.type, (state, { payload }) => {
      const newAllLivestreams = [];
      (state.livestreams.allLivestreams ?? []).forEach((s1) => {
        newAllLivestreams.push(s1);
      });
      payload.livestreams.allLivestreams.forEach((s1) => {
        if (!newAllLivestreams.includes(s1)) {
          newAllLivestreams.push(s1);
        }
      });
      return {
        ...state,
        isLoading: false,
        pagination: payload.pagination,
        livestreams: {
          allLivestreams: newAllLivestreams,
          livestreamsById: {
            ...(state.livestreams.livestreamsById ?? {}),
            ...payload.livestreams.livestreamsById,
          },
        },
      };
    })
    .addCase(getLivestreamsFailure.type, (state, { payload }) => ({
      ...state,
      isSuccess: false,
      isLoading: false,
      error: payload,
    }))
    // reset livestream
    .addCase(resetLivestreams.type, (state) => ({
      ...state,
      livestreams: {},
    }))
    //joinLivestream
    .addCase(joinLivestream.type, (state, { payload }) => ({
      ...state,
      isJoining: true,
      activeStream: { ...state.activeStream, ...payload, isLoading: false },
    }))
    .addCase(joinLivestreamSuccess.type, (state, { payload }) => {
      return {
        ...state,
        isJoining: false,
        activeStream: {
          ...state.activeStream,
          ...payload,
          isLoading: false,
          isSuccess: true,
        },
      };
    })
    .addCase(joinLivestreamFailure.type, (state, { payload }) => {
      if (payload.code === BANNED_ERROR_CODE) {
        return {
          ...state,
          isJoining: false,
          activeStream: {
            ...state.activeStream,
            streamParticipant: {
              ...state.activeStream?.streamParticipant,
              isBanned: true,
            },
          },
        };
      }

      return {
        ...state,
        isJoining: false,
      };
    })

    //leaveLivestream
    .addCase(leaveLivestream.type, (state, { payload }) => ({
      ...state,
      activeStream: { ...state.activeStream, ...payload, isLoading: false },
    }))
    .addCase(leaveLivestreamSuccess.type, (state) => {
      return {
        ...state,
        activeStream: {
          ...state.activeStream,
          streamParticipant: {
            ...state.activeStream.streamParticipant,
            isCoStreamer: false,
          },
          token: null,
        },
        chat: initialState.chat,
      };
    })
    .addCase(leaveLivestreamFailure.type, (state) => ({
      ...state,
    }))
    //sendMessage
    .addCase(sendMessage.type, (state) => ({
      ...state,
      chat: {
        ...state.chat,
        isLoading: true,
      },
    }))
    .addCase(sendMessageSuccess.type, (state) => {
      return {
        ...state,
        chat: {
          ...state.chat,
          isLoading: false,
        },
      };
    })
    .addCase(sendMessageFailure.type, (state, { payload }) => ({
      ...state,
      chat: {
        ...state.chat,
        isLoading: false,
      },
      error: payload?.error,
    }))
    //sendMessage
    .addCase(getMessages.type, (state) => ({
      ...state,
      chat: {
        ...state.chat,
      },
    }))
    .addCase(getMessagesSuccess.type, (state, { payload }) => {
      return {
        ...state,
        chat: {
          ...state.chat,
          ...payload,
        },
      };
    })
    .addCase(getMessagesFailure.type, (state, { payload }) => ({
      ...state,
      chat: {
        ...state.chat,
      },
      error: payload?.error,
    }))

    //getParticipants
    .addCase(getParticipants.type, (state) => {
      return {
        ...state,
        participants: {
          ...state.participants,
          isLoading: true,
        },
      };
    })
    .addCase(getTopTippersSuccess.type, (state, { payload }) => {
      return {
        ...state,
        topTippers: [...payload],
      };
    })
    .addCase(getParticipantsSuccess.type, (state, { payload }) => {
      const { participants } = state;
      const { participantsById } = participants;
      const {
        allParticipants: newAllParticipants,
        participantsById: newParticipantsById,
        totalCount,
      } = payload;
      return {
        ...state,
        participants: {
          ...state.participants,
          allParticipants: [...new Set([...newAllParticipants])].sort(
            (first, second) => second - first,
          ),
          participantsById: { ...participantsById, ...newParticipantsById },
          totalCount,
          error: initialState.error,
          isLoading: false,
        },
      };
    })
    .addCase(getParticipantsFailure.type, (state) => {
      return {
        ...state,
        participants: {
          ...state.participants,
          isLoading: false,
        },
      };
    })

    //acceptInviteActionTypes
    .addCase(acceptInvite.type, (state) => {
      return {
        ...state,
      };
    })
    .addCase(acceptInviteSuccess.type, (state, { payload }) => {
      const invites = state.invites.filter(({ id }) => id !== payload.id);
      return {
        ...state,
        invites,
      };
    })
    .addCase(acceptInviteFailure.type, (state) => {
      return {
        ...state,
      };
    })

    //requestInviteToCoStreaming
    .addCase(requestInviteToCoStreaming.type, (state) => {
      return {
        ...state,
      };
    })
    .addCase(requestInviteToCoStreamingSuccess.type, (state) => {
      return {
        ...state,
      };
    })
    .addCase(requestInviteToCoStreamingFailure.type, (state) => {
      return {
        ...state,
      };
    })
    //makeCoStreamerAViewer
    .addCase(makeCoStreamerAViewer.type, (state) => {
      return {
        ...state,
      };
    })
    .addCase(makeCoStreamerAViewerSuccess.type, (state) => {
      return {
        ...state,
      };
    })
    .addCase(makeCoStreamerAViewerFailure.type, (state) => {
      return {
        ...state,
      };
    })

    //rejectInviteActionTypes
    .addCase(rejectInvite.type, (state) => {
      return {
        ...state,
      };
    })
    .addCase(rejectInviteSuccess.type, (state, { payload }) => {
      return {
        ...state,
        invites: state.invites.filter(({ id }) => id !== payload.id),
      };
    })
    .addCase(rejectInviteFailure.type, (state, { payload }) => {
      return {
        ...state,
        invites: state.invites.filter(({ id }) => id !== payload.id),
      };
    })
    //rejectInviteActionTypes
    .addCase(rejectAll.type, (state) => {
      return {
        ...state,
      };
    })
    .addCase(rejectAllSuccess.type, (state) => {
      return {
        ...state,
        invites: [],
      };
    })
    .addCase(rejectAllFailure.type, (state) => {
      return {
        ...state,
      };
    })
    .addCase(changeParticipantRoleSuccess.type, (state, { payload }) => {
      const participantId = getParticipantId(payload.participant);
      return {
        ...state,
        chat: {
          ...state.chat,
          messages: [...payload.messages],
          isLoading: false,
        },
        participants: {
          ...state.participants,
          participantsById: {
            ...state.participants.participantsById,
            [participantId]: {
              ...state.participants.participantsById[participantId],
              role: payload.participant.role,
            },
          },
        },
      };
    })
    .addCase(banParticipantSuccess.type, (state, { payload }) => {
      const { id, listType, totalTipsAmount } = payload;
      const participantId = getParticipantId({ id, listType });
      const participantsByIdCopy = {
        ...state.participants.participantsById,
      };
      if (!totalTipsAmount) delete participantsByIdCopy[participantId];
      return {
        ...state,
        chat: {
          ...state.chat,
          isLoading: false,
        },
        participants: {
          isLoading: false,
          allParticipants: !totalTipsAmount
            ? state.participants.allParticipants.filter(
                (_id) => _id !== participantId,
              )
            : state.participants.allParticipants,
          participantsById: participantsByIdCopy,
        },
      };
    })
    .addCase(handleDeleteMessageSuccess.type, (state, { payload }) => {
      return {
        ...state,
        chat: {
          ...state.chat,
          messages: state.chat.messages.filter(
            ({ id }) => id !== payload?.deletedMessageId,
          ),
          isLoading: false,
        },
      };
    })
    .addCase(
      handleInviteFromStreamerToViewerSuccess.type,
      (state, { payload }) => {
        return {
          ...state,
          invites: [
            ...state.invites,
            {
              ...payload.invite,
              streamParticipant: { ...payload.streamParticipant },
            },
          ],
        };
      },
    )
    .addCase(
      handleHostAcceptedRequestToCoStreamingSuccess.type,
      (state, { payload }) => {
        const updatedInviteList = [...state.inviteList.list];
        const userIndex = updatedInviteList.findIndex(
          (u) => u.id === payload.streamParticipant.id,
        );
        if (updatedInviteList[userIndex]) {
          updatedInviteList[userIndex] = {
            ...updatedInviteList[userIndex],
            streamInvite: {
              ...updatedInviteList[userIndex].streamInvite,
              status: 2,
            },
          };
        }
        return {
          ...state,
          activeStream: {
            ...state.activeStream,
            streamParticipant: {
              ...state.activeStream.streamParticipant,
              isHostAcceptedRequest: true,
            },
          },
          inviteList: {
            ...state.inviteList,
            list: updatedInviteList,
          },
        };
      },
    )
    .addCase(handleCoStreamerStartsStreamSuccess.type, (state, { payload }) => {
      const updatedInviteList = [...state.inviteList.list];
      const userIndex = updatedInviteList.findIndex(
        (u) => u.id === payload.participant.id,
      );
      if (updatedInviteList[userIndex]) {
        updatedInviteList[userIndex] = {
          ...updatedInviteList[userIndex],
          ...payload.participant,
        };
      }
      return {
        ...state,
        inviteList: {
          ...state.inviteList,
          list: updatedInviteList,
        },
      };
    })
    .addCase(handleBanParticipantSuccess.type, (state, { payload }) => {
      return updateParticipant(state, payload);
    })
    .addCase(handleChangeCoStreamerRoleToViewerSuccess.type, (state) => {
      return {
        ...state,
        activeStream: {
          ...state.activeStream,
          streamParticipant: {
            ...state.activeStream.streamParticipant,
            isRoleChangedToViewer: true,
          },
        },
      };
    })
    .addCase(handleMuteParticipantSuccess.type, (state, { payload }) => {
      return updateParticipant(state, payload);
    })
    .addCase(
      handleDeleteAllParticipantMessagesSuccess.type,
      (state, { payload }) => {
        return {
          ...state,
          chat: {
            ...state.chat,
            messages: state.chat.messages.filter(
              (message) =>
                message?.streamParticipant.id !== payload?.streamParticipantId,
            ),

            isLoading: false,
          },
        };
      },
    )
    .addCase(getNewMessageSuccess.type, (state, { payload }) => {
      const { message, amountsList } = payload;
      const isTip = message?.type === messageTypes.tip;

      const animation = amountsList.find(
        ({ amount, testAmount }) =>
          amount === +message.text || testAmount === +message.text,
      );

      const duration = animation?.duration ?? 5000;

      const animationStartsAt =
        state.lastAnimationFinishedAt &&
        new Date() < new Date(state.lastAnimationFinishedAt)
          ? state.lastAnimationFinishedAt
          : new Date();

      const animationFinishedAt = new Date(+animationStartsAt + duration);

      const newLastAnimationFinishedAt =
        new Date(state.lastAnimationFinishedAt) < new Date(animationFinishedAt)
          ? animationFinishedAt
          : state.lastAnimationFinishedAt;
      const newTips = isTip
        ? [
            ...state.tips,
            {
              ...message,
              animationStartsAt,
              animationFinishedAt,
            },
          ]
        : [...state.tips];

      return {
        ...state,
        chat: {
          ...state.chat,
          messages: [...state.chat.messages, message],
          isLoading: false,
        },
        tips: [...newTips],
        lastAnimationFinishedAt: newLastAnimationFinishedAt,
        lastAnimationCategory: animation?.category,
      };
    })
    .addCase(setTipsList.type, (state, { payload }) => {
      return {
        ...state,
        tips: [...payload],
      };
    })
    .addCase(getParticipantsUpdateSuccess.type, (state, { payload }) => {
      return {
        ...state,
        activeStream: {
          ...state.activeStream,
          streamParticipant: {
            ...state.activeStream.streamParticipant,
            ...payload.participant,
            roleWasChanged: true,
          },
        },
      };
    })
    .addCase(resetRoleWasChangedFlag.type, (state) => {
      return {
        ...state,
        activeStream: {
          ...state.activeStream,
          streamParticipant: {
            ...state.activeStream.streamParticipant,
            roleWasChanged: false,
          },
        },
      };
    })
    .addCase(resetActiveStream.type, (state) => {
      return {
        ...state,
        activeStream: {},
      };
    })
    .addCase(getNewTipSuccess.type, (state) => {
      return {
        ...state,
        chat: {
          ...state.chat,
          isLoading: false,
        },
      };
    })
    .addCase(deleteMessageSuccess.type, (state, { payload }) => {
      return {
        ...state,
        chat: {
          ...state.chat,
          messages: [...payload],
          isLoading: false,
        },
      };
    })
    .addCase(deleteAllParticipantMessagesSuccess.type, (state, { payload }) => {
      return {
        ...state,
        chat: {
          ...state.chat,
          messages: [...payload],
          isLoading: false,
        },
      };
    })
    .addCase(getLivestreamDetailsSuccess.type, (state, { payload }) => {
      return {
        ...state,
        activeStream: {
          ...state.activeStream,
          ...payload,
          title: payload?.title ?? "",
          description: payload?.description ?? "",
          previewImages: state.activeStream?.previewImages?.length
            ? state?.activeStream?.previewImages
            : payload?.previewImages || [],
        },
        isLoading: false,
      };
    })
    .addCase(getUpdatedLivestreamDetailsSuccess.type, (state, { payload }) => {
      return {
        ...state,
        activeStream: {
          ...state.activeStream,
          ...payload,
          title: payload?.title ?? "",
          description: payload?.description ?? "",
        },
        isLoading: false,
      };
    })
    .addCase(livestreamEndedSuccess.type, (state) => {
      const activeStream = {
        ...state.activeStream,
        status: livestreamStatuses.ended,
      };
      return {
        ...state,
        activeStream,
        chat: initialState.chat,
        isLoading: false,
      };
    })
    .addCase(unmuteMe.type, (state) => {
      return {
        ...state,
        activeStream: {
          ...state.activeStream,
          streamParticipant: {
            ...state.activeStream.streamParticipant,
            isMuted: false,
          },
        },
      };
    })
    //getInviteList
    .addCase(getInviteList.type, (state) => {
      return {
        ...state,
        inviteList: {
          ...state.inviteList,
          isLoading: true,
        },
      };
    })
    .addCase(getInviteListSuccess.type, (state, { payload }) => {
      return {
        ...state,
        inviteList: {
          isLoading: false,
          list: [...payload],
        },
      };
    })
    .addCase(getInviteListFailure.type, (state) => {
      return {
        ...state,
        inviteList: {
          ...state.inviteList,
          isLoading: false,
        },
      };
    })
    //inviteParticipant
    .addCase(inviteParticipant.type, (state) => {
      return {
        ...state,
        inviteList: {
          ...state.inviteList,
          isInviting: true,
        },
      };
    })
    .addCase(inviteParticipantSuccess.type, (state, { payload }) => {
      const { id, data } = payload;

      return {
        ...state,
        inviteList: {
          list: [
            ...state.inviteList.list.map((el) => (el?.id === id ? data : el)),
          ],
          isInviting: false,
        },
      };
    })
    .addCase(inviteParticipantFailure.type, (state) => {
      return {
        ...state,
        inviteList: {
          ...state.inviteList,
          isInviting: false,
        },
      };
    })
    .addCase(makeButtonInvitableActionType, (state, { payload }) => {
      const { id } = payload;
      return {
        ...state,
        inviteList: {
          ...state.inviteList,
          list: [
            ...state.inviteList.list.map((el) =>
              el?.id === id
                ? removeEmptyKeys({
                    ...el,
                    isInviteAllow: true,
                    nextInviteAt: undefined,
                  })
                : { ...el },
            ),
          ],
        },
      };
    })
    .addCase(updateLivestreamGoal.type, (state) => {
      return {
        ...state,
        isLoading: true,
      };
    })
    .addCase(updateLivestreamGoalSuccess.type, (state, { payload }) => {
      return {
        ...state,
        activeStream: {
          ...state.activeStream,
          ...payload,
        },
        isLoading: false,
      };
    })
    .addCase(updateLivestreamGoalFailure.type, (state) => {
      return {
        ...state,
        isLoading: false,
      };
    })
    .addCase(deleteLivestreamGoal.type, (state) => {
      return {
        ...state,
        isLoading: true,
      };
    })
    .addCase(deleteLivestreamGoalSuccess.type, (state) => {
      return {
        ...state,
        activeStream: {
          ...state.activeStream,
          streamGoal: {
            ...initialState.activeStream.streamGoal,
          },
        },
        isLoading: false,
      };
    })
    .addCase(deleteLivestreamGoalFailure.type, (state) => {
      return {
        ...state,
        isLoading: false,
      };
    })
    .addCase(updateSpinTheBottleActionType, (state, { payload }) => {
      return {
        ...state,
        activeStream: {
          ...state.activeStream,
          lastSpinTheBottleEvent: payload,
        },
      };
    });
});
